import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, DollarSign, Plus, RotateCcw, Trophy, XCircle } from "lucide-react";
import useTranslation from "../../../client/hooks/useTranslation";
import { useLiteData } from "../../data/LiteDataProvider";
import { Pnl } from "../../ui/ui";
import type { EventKind, PfmEvent } from "./pfmModel";

/** ROI signé (« +12 % »), ou tiret sans dépense. */
export function RoiText({ roi, className = "" }: { roi: number | null; className?: string }) {
  const { fmt } = useLiteData();
  if (roi === null) return <span className={`font-mono tabular-nums text-muted-foreground/70 ${className}`}>-</span>;
  const tone = roi >= 0 ? "text-pnlPositive" : "text-pnlNegative";
  return <span className={`font-mono tabular-nums ${tone} ${className}`}>{`${roi >= 0 ? "+" : "−"}${fmt.pct(Math.abs(roi) / 100)}`}</span>;
}

export function roiLabel(roi: number | null, pct: (v: number) => string) {
  return roi === null ? "-" : `${roi >= 0 ? "+" : "−"}${pct(Math.abs(roi) / 100)}`;
}

export function Legend({ color, label, line }: { color: string; label: string; line?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`${line ? "w-4 h-0.5" : "w-2.5 h-2.5 rounded-sm"} ${color}`} />
      {label}
    </span>
  );
}

export const EVENT_STYLE: Record<EventKind, { icon: ReactNode; cls: string }> = {
  payout: { icon: <Plus size={12} />, cls: "bg-pnlPositive/15 text-pnlPositive" },
  challenge: { icon: <DollarSign size={12} />, cls: "bg-pnlNegative/15 text-pnlNegative" },
  activation: { icon: <DollarSign size={12} />, cls: "bg-pnlNegative/15 text-pnlNegative" },
  oneOff: { icon: <DollarSign size={12} />, cls: "bg-pnlNegative/15 text-pnlNegative" },
  recurring: { icon: <CalendarClock size={12} />, cls: "bg-warning/15 text-warning" },
  funded: { icon: <Trophy size={12} />, cls: "bg-primary/15 text-primary" },
  passed: { icon: <Trophy size={12} />, cls: "bg-pnlPositive/15 text-pnlPositive" },
  reset: { icon: <RotateCcw size={12} />, cls: "bg-muted text-muted-foreground" },
  breached: { icon: <XCircle size={12} />, cls: "bg-pnlNegative/15 text-pnlNegative" },
  closed: { icon: <XCircle size={12} />, cls: "bg-muted text-muted-foreground" },
};

/** Pastille pleine (mobile) de chaque événement. */
export const EVENT_DOT: Record<EventKind, string> = {
  payout: "bg-pnlPositive", challenge: "bg-pnlNegative", activation: "bg-pnlNegative", oneOff: "bg-pnlNegative",
  recurring: "bg-warning", funded: "bg-primary", passed: "bg-pnlPositive", reset: "bg-muted-foreground",
  breached: "bg-pnlNegative", closed: "bg-muted-foreground",
};

export function useEventLabel() {
  const t = useTranslation().lite.pfm.events;
  return (e: PfmEvent) => t[e.kind](e.name);
}

export function EventRow({ e }: { e: PfmEvent }) {
  const { fmt } = useLiteData();
  const label = useEventLabel();
  const st = EVENT_STYLE[e.kind];
  const body = (
    <>
      <span className={`w-6 h-6 rounded-md grid place-items-center shrink-0 ${st.cls}`}>{st.icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate">{label(e)}</div>
        <div className="text-xs text-muted-foreground/70">{fmt.dateShort(e.date)}</div>
      </div>
      {e.amount !== undefined && <Pnl value={e.amount} className="text-sm" />}
    </>
  );
  return e.accountId
    ? <Link to={`/account/${e.accountId}`} className="flex items-center gap-3 px-5 py-2.5 hover:bg-muted/60 transition-colors">{body}</Link>
    : <div className="flex items-center gap-3 px-5 py-2.5">{body}</div>;
}
